import { isDevelopment } from '@/lib/utils'

import type { TrackedEvent } from './events'
import { resolveTrackingPosition } from './tracking-position'

const isDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  // Reading `localStorage` throws SecurityError in storage-blocked contexts
  // (mobile browsers with site data blocked, sandboxed embeds). `typeof` does
  // not guard a throwing getter, so the access must be wrapped in try/catch —
  // otherwise this throws uncaught from the analytics-init effect.
  try {
    if (localStorage.getItem('htAnalyticsDebug') === '1') {
      return true
    }
  } catch {
    // storage unavailable — fall through to the URL-param check
  }
  if (typeof URLSearchParams !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    if (params.get('dev') === 'analytics') {
      return true
    }
  }
  return false
}

let debugStyleInjected = false
let tooltipEl: HTMLDivElement | null = null
let hoverCleanup: (() => void) | null = null

const injectDebugStyles = () => {
  if (debugStyleInjected) {
    return
  }
  debugStyleInjected = true
  const style = document.createElement('style')
  style.textContent = `
    [data-track] { outline: 2px dashed rgba(255, 0, 0, 0.6) !important; position: relative; }
    [data-track-impression] { outline: 2px dashed rgba(0, 128, 255, 0.6) !important; }
    [data-track][data-track-impression] { outline: 2px dashed rgba(128, 0, 255, 0.6) !important; }
  `
  document.head.appendChild(style)
}

const getOrCreateTooltip = (): HTMLDivElement => {
  if (tooltipEl) {
    return tooltipEl
  }
  tooltipEl = document.createElement('div')
  tooltipEl.style.cssText = `
    position: fixed; z-index: 99999; background: #1a1a2e; color: #e0e0e0;
    border: 1px solid #555; border-radius: 6px; padding: 8px 12px;
    font: 11px/1.4 monospace; max-width: 400px; pointer-events: none;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3); display: none; white-space: pre-wrap;
  `
  document.body.appendChild(tooltipEl)
  return tooltipEl
}

export const extractTrackingProps = (el: HTMLElement): Record<string, string> => {
  const props: Record<string, string> = {}
  for (const [key, value] of Object.entries(el.dataset)) {
    if (key.startsWith('event') || key.startsWith('affiliate') || key === 'track' || key === 'trackImpression') {
      props[key] = value ?? ''
    }
  }

  const position = resolveTrackingPosition(el)
  if (position !== undefined) {
    props.eventPosition = position
  }

  const listicleParent = el.closest('[data-listicle-ordering]')
  if (listicleParent instanceof HTMLElement && listicleParent.dataset.listicleOrdering) {
    props.listicleOrdering = listicleParent.dataset.listicleOrdering
  }

  const posParent = el.closest('[data-event-listicle-position]')
  if (posParent instanceof HTMLElement && posParent.dataset.eventListiclePosition) {
    props.listiclePosition = posParent.dataset.eventListiclePosition
  }

  return props
}

const setupDebugOverlay = () => {
  injectDebugStyles()
  const tip = getOrCreateTooltip()

  const onMouseOver = (e: MouseEvent) => {
    const target = (e.target as HTMLElement).closest('[data-track], [data-track-impression]')
    if (!(target instanceof HTMLElement)) {
      tip.style.display = 'none'
      return
    }
    const props = extractTrackingProps(target)
    const lines = Object.entries(props).map(([k, v]) => `${k}: ${v}`)
    tip.textContent = lines.join('\n') || '(no tracking props)'
    tip.style.display = 'block'

    const rect = target.getBoundingClientRect()
    const tipX = Math.min(rect.left, window.innerWidth - 420)
    const tipY = rect.bottom + 4
    tip.style.left = `${Math.max(0, tipX)}px`
    tip.style.top = `${tipY}px`
  }

  const onMouseOut = (e: MouseEvent) => {
    const related = e.relatedTarget as HTMLElement | null
    if (!related?.closest('[data-track], [data-track-impression]')) {
      tip.style.display = 'none'
    }
  }

  document.addEventListener('mouseover', onMouseOver)
  document.addEventListener('mouseout', onMouseOut)

  hoverCleanup = () => {
    document.removeEventListener('mouseover', onMouseOver)
    document.removeEventListener('mouseout', onMouseOut)
    tip.style.display = 'none'
  }
}

export const initAudit = () => {
  if (!isDebugEnabled()) {
    return
  }
  setupDebugOverlay()
}

export const cleanupAudit = () => {
  hoverCleanup?.()
  hoverCleanup = null
}

// `sent` = the event was queued for delivery to PostHog. Impressions that lose
// the sampling roll come through with sent=false so you can still see them fire.
export const logEvent = (tracked: TrackedEvent, sent: boolean) => {
  // Local dev always logs so tracking is verifiable in the console with no
  // opt-in. Outside dev it stays behind the debug flag (?dev=analytics or
  // localStorage htAnalyticsDebug=1) to keep prod/staging consoles quiet.
  if (!isDevelopment && !isDebugEnabled()) {
    return
  }
  const status = sent ? 'sent → PostHog' : 'NOT sent (dropped by impression sampling)'
  console.groupCollapsed(`[analytics] ${tracked.event} (${tracked.eventType}) — ${status}`)
  console.log('properties:', tracked.properties)
  console.log('timestamp:', new Date(tracked.timestamp).toISOString())
  console.groupEnd()
}
